import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { LogOut, Settings, User, Users, Download, LayoutList, FileText, Sparkles } from 'lucide-react';
import { useSession, useLogout } from '../api/session';
import { useGroups } from '../api/hierarchy';
import { api } from '../api/client';
import { usePublicConfig } from '../api/public-config';
import { SearchBar } from './SearchBar';
import { NotificationBell } from './NotificationCenter';
import { AdBanner } from './AdBanner';

interface StaticPageRef {
  slug: string;
  title: string;
  pageType: 'LEGAL' | 'HELP' | 'COMING_SOON' | 'CUSTOM';
}

const PAGE_TYPE_ORDER = ['HELP', 'CUSTOM', 'COMING_SOON', 'LEGAL'] as const;
const PAGE_TYPE_LABELS: Record<string, string> = {
  HELP: 'Help',
  CUSTOM: 'About',
  COMING_SOON: 'Coming soon',
  LEGAL: 'Legal',
};

function groupPages(pages: StaticPageRef[]): Record<string, StaticPageRef[]> {
  const out: Record<string, StaticPageRef[]> = {};
  for (const t of PAGE_TYPE_ORDER) out[t] = [];
  for (const p of pages) (out[p.pageType] ??= []).push(p);
  // Drop empty groups so the menu doesn't render headers with nothing under them.
  for (const k of Object.keys(out)) if ((out[k]?.length ?? 0) === 0) delete out[k];
  return out;
}

function labelFor(type: string): string {
  return PAGE_TYPE_LABELS[type] ?? type;
}

export function Header(): React.ReactElement {
  const { data: session } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const { groupSlug } = useParams();
  const { data: groups } = useGroups();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const tiersVisible = usePublicConfig((s) => s.config.tiersVisible);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Lazy-load the page directory only when the dropdown opens for the first
  // time - keeps the initial header render free of an extra fetch.
  const { data: pagesData } = useQuery({
    queryKey: ['static-pages-directory'],
    queryFn: () => api.get('pages').json<{ pages: StaticPageRef[] }>(),
    enabled: menuOpen,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [menuOpen]);

  const user = session?.user;

  return (
    <header className="sl-header">
      {user && <AdBanner slot="HEADER" />}
      <div className="sl-header-inner">
        <RouterLink to="/" className="sl-logo">
          <img src="/default-icon.png" alt="" className="sl-logo-icon" /> SlapLink
        </RouterLink>

        {user ? (
          <>
            <div className="sl-header-center">
              {(groups?.length ?? 0) > 0 && (
                <select
                  className="sl-group-selector"
                  value={groupSlug ?? groups?.[0]?.slug ?? ''}
                  onChange={(e) => navigate(`/links/${e.target.value}`)}
                >
                  {(groups ?? []).map((g) => (
                    <option key={g.id} value={g.slug}>
                      {g.name}
                    </option>
                  ))}
                </select>
              )}
              <SearchBar />
            </div>

            <div className="sl-header-right">
              <NotificationBell />
              <div ref={menuRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="sl-user-avatar-btn"
                  onClick={() => setMenuOpen((v) => !v)}
                  title={`Signed in as ${user.username}`}
                  aria-label={`Account menu for ${user.username}`}
                >
                  {(user.username || '?').charAt(0).toUpperCase()}
                </button>
                {menuOpen && (
                  <div className="sl-user-menu">
                    {/* Identity card so people never act in the wrong account.
                        Username + email truncate; full email in the title attr. */}
                    <div className="sl-user-menu-id" title={user.email}>
                      <span className="sl-user-menu-avatar">
                        {(user.username || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="sl-user-menu-id-stack">
                        <span className="sl-user-menu-name">{user.username}</span>
                        <span className="sl-user-menu-email">{user.email}</span>
                      </span>
                    </div>
                    <div className="sl-user-menu-sep" />
                    <RouterLink to="/account" onClick={() => setMenuOpen(false)}>
                      <User size={14} /> Account
                    </RouterLink>
                    <RouterLink to="/config" onClick={() => setMenuOpen(false)}>
                      <Settings size={14} /> Settings
                    </RouterLink>
                    <RouterLink to="/contacts" onClick={() => setMenuOpen(false)}>
                      <Users size={14} /> Contacts
                    </RouterLink>
                    <RouterLink to="/manage-sets" onClick={() => setMenuOpen(false)}>
                      <LayoutList size={14} /> Manage
                    </RouterLink>
                    <RouterLink to="/import-export" onClick={() => setMenuOpen(false)}>
                      <Download size={14} /> Import / export
                    </RouterLink>
                    {tiersVisible && (
                      <RouterLink to="/tiers" onClick={() => setMenuOpen(false)}>
                        <Sparkles size={14} /> Plans &amp; tiers
                      </RouterLink>
                    )}
                    {(pagesData?.pages.length ?? 0) > 0 && (
                      <>
                        <div className="sl-user-menu-sep" />
                        <button
                          type="button"
                          className="sl-user-menu-section-toggle"
                          aria-expanded={pagesOpen}
                          onClick={() => setPagesOpen((v) => !v)}
                        >
                          <FileText size={14} /> Help &amp; info
                          <span className="sl-user-menu-caret" data-open={pagesOpen}>›</span>
                        </button>
                        {pagesOpen && (
                          <div className="sl-user-menu-pages">
                            {Object.entries(groupPages(pagesData!.pages)).map(([type, list]) => (
                              <div key={type} className="sl-user-menu-page-group">
                                <div className="sl-user-menu-page-label">{labelFor(type)}</div>
                                {list.map((p) => (
                                  <RouterLink
                                    key={p.slug}
                                    to={`/p/${p.slug}`}
                                    className="sl-user-menu-page-link"
                                    onClick={() => setMenuOpen(false)}
                                  >
                                    {p.title}
                                  </RouterLink>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <div className="sl-user-menu-sep" />
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        logout.mutate(undefined, { onSuccess: () => navigate('/login') });
                      }}
                    >
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <nav className="sl-header-right">
            <RouterLink to="/login" className="sl-btn sl-btn-ghost sl-btn-sm">Login</RouterLink>
            <RouterLink to="/signup" className="sl-btn sl-btn-primary sl-btn-sm">Sign up</RouterLink>
          </nav>
        )}
      </div>
    </header>
  );
}
